import { FilterCondition } from "@irohana-dev/components";
import { FC, useCallback } from "react";
import { TSavedConditionShareScope } from "@/api/services/main/saved-condition/dtos/saved-condition.dto";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { useFilterConditionPanel } from "@/components/commons/filter-condition/use-filter-condition-panel";
import { TFilterConditionPanelConfig } from "@/components/commons/filter-condition/types";
import { CaseListFilterFormFields } from "@/features/case/components/CaseList/CaseListFilterFormFields";
import { fromFilterValue, toFilterValue } from "@/features/case/components/CaseList/case-list-filter-condition-adapter";
import {
  LOCKED_CASE_LIST_COLUMN_IDS,
  TCaseListDisplayColumnSetting,
  TCaseListFilterFormExtended,
  defaultCaseListDisplayColumns,
  defaultCaseListFilterValues,
  normalizeCaseDateFields,
  normalizeMultiSelectFields,
  stripEmptyCaseListFilterFields,
} from "@/features/case/components/CaseList/case-list-filter-shared";
import { CASE_LIST_DISPLAY_COLUMNS_STORAGE_KEY } from "@/features/case/constants/storage-keys";
import { filterConditionShareColor } from "@/components/commons/filter-condition/filter-condition-share-color";
import { FILTER_CONDITION_PANEL_WIDTH } from "@/components/commons/filter-condition/filter-condition-panel-width";

const panelConfig: TFilterConditionPanelConfig<TCaseListFilterFormExtended, TCaseListDisplayColumnSetting> = {
  pageType: "case",
  defaultFilters: defaultCaseListFilterValues,
  defaultColumns: defaultCaseListDisplayColumns,
  columnsStorageKey: CASE_LIST_DISPLAY_COLUMNS_STORAGE_KEY,
  lockedColumnIds: LOCKED_CASE_LIST_COLUMN_IDS,
  normalizeFilters: (filters) => normalizeMultiSelectFields(normalizeCaseDateFields(filters)) as Partial<TCaseListFilterFormExtended>,
  toFilterValue,
  fromFilterValue,
  stripEmptyFilters: stripEmptyCaseListFilterFields,
  getDateRange: (filters) => ({ from: filters.registrationDateFrom, to: filters.registrationDateTo }),
};

type Props = {
  isOpen: boolean;
  mode?: "create" | "edit";
  onClose: () => void;
  onApplyFilters?: (filters: TCaseListFilterFormExtended) => void;
  onApplyDisplaySettings?: (columns: TCaseListDisplayColumnSetting[]) => void;
  initialFilters?: Partial<TCaseListFilterFormExtended>;
  initialDisplayColumns?: TCaseListDisplayColumnSetting[];
  appliedConditionId?: number | null;
  appliedConditionName?: string;
  appliedConditionShareScope?: TSavedConditionShareScope;
  appliedConditionIsDefault?: boolean;
  appliedConditionIncludeColumnSettings?: boolean;
  appliedConditionColumnSettings?: { order?: string[]; hidden?: string[] } | null;
  onConditionSaved?: (id: number, title: string) => void;
};

export const CaseListFilterConditionPanel: FC<Props> = ({ mode = "create", ...rest }) => {
  const panel = useFilterConditionPanel(panelConfig, { ...rest, mode });

  const handleFilterChange = useCallback(
    (field: keyof TCaseListFilterFormExtended, value: unknown) => {
      panel.setValue((prev) => toFilterValue({ ...fromFilterValue(prev), [field]: value }));
    },
    [panel.setValue],
  );

  return (
    <>
      <FilterCondition
        width={FILTER_CONDITION_PANEL_WIDTH}
        shareColor={filterConditionShareColor}
        isOpen={rest.isOpen}
        mode={panel.displayMode}
        onClose={rest.onClose}
        columns={panel.columnOptions}
        lockedColumns={LOCKED_CASE_LIST_COLUMN_IDS}
        columnOrder={panel.columnOrder}
        hiddenColumns={panel.hiddenColumns}
        onColumnSettingsChange={panel.handleColumnSettingsChange}
        conditionName={panel.conditionName}
        onConditionNameChange={panel.handleConditionNameChange}
        conditionNameError={panel.conditionNameError}
        appliedConditionId={rest.appliedConditionId ?? undefined}
        shareScope={panel.shareScope}
        onShareScopeChange={panel.setShareScope}
        isDefault={panel.isDefault}
        onIsDefaultChange={panel.setIsDefault}
        includeColumnSettings={panel.includeColumnSettings}
        onIncludeColumnSettingsChange={panel.setIncludeColumnSettings}
        hasSavedColumnSettings={panel.hasSavedColumnSettings}
        onSaveCondition={panel.handleSaveCondition}
        isSaving={panel.isSaving}
        onApply={panel.handleApply}
        onClear={() => panel.hasDataToClear && panel.setIsClearConfirmOpen(true)}
      >
        <CaseListFilterFormFields filterValues={panel.currentFilters} onFilterChange={handleFilterChange} />
      </FilterCondition>
      <ConfirmDeleteModal
        open={panel.isClearConfirmOpen}
        onClose={() => panel.setIsClearConfirmOpen(false)}
        onOk={panel.clearValue}
        message="設定した条件がすべてクリアされますがよろしいですか？"
        title=""
      />
    </>
  );
};
